import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import { tokenService } from "@/auth/tokenService";
import { ApiError } from "@/apis/proda_finance";
import { MainLayout } from "@/ui/layouts";
import { Loader } from "@/ui/atoms";
import {
    AnnouncementBanner,
    FeatureFlagsModal,
    FeedbackButton,
    type TUserFeatureFlagsData,
} from "@/ui/organisms";
import { computeIsViewingAsAdmin } from "@/store/auth/sliceAuth";
import { resolveLandingPath, routerPaths } from "@/ui/router";
import type { TCompositionDeps } from "./types";

export const wireRootLayout = (
    { store, useAppStore, api }: TCompositionDeps,
    ViewSettings: ReturnType<typeof import("@/ui/views").createViewSettings>,
) => {
    // ==========================================
    // Access Error Page
    // ==========================================
    // Full-page notice shown when the user cannot use the app at all — either
    // their email domain is unsupported (soft 200 error from /auth/me) or their
    // account has been deactivated (403 USER_INACTIVE).
    const AccessErrorPage = ({
        title,
        email,
        errorMessage,
        onLogout,
    }: {
        title: string;
        email?: string;
        errorMessage?: string;
        onLogout: () => void;
    }) => (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
            <div className="max-w-md text-center p-8 rounded-2xl bg-slate-800/50 border border-slate-700 shadow-2xl">
                <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-8 w-8 text-amber-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
                </div>

                <p className="text-slate-300 mb-6 leading-relaxed">
                    {errorMessage ||
                        "Your email domain is not supported. Please contact support for assistance."}
                </p>

                {email && (
                    <p className="text-sm text-slate-400 mb-6">
                        Signed in as: <span className="text-slate-200">{email}</span>
                    </p>
                )}

                <div className="space-y-3">
                    <a
                        href="mailto:support@proda.ai"
                        className="block w-full px-6 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-500 transition-colors"
                    >
                        Contact support@proda.ai
                    </a>
                    <button
                        onClick={onLogout}
                        className="w-full px-6 py-3 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
                    >
                        Sign out and try another account
                    </button>
                </div>
            </div>
        </div>
    );

    // Routes that require admin role. Users and Announcements are now gated by
    // specific permissions (see routePermissions below), not admin role.
    const adminOnlyRoutes: string[] = ["/files/archive"];

    // Routes that require a specific permission (checked via startsWith)
    const routePermissions: Record<string, string> = {
        "/admin/users": "users_view",
        "/settings/chart-of-accounts": "settings_coa_view",
        "/settings/propcodes": "settings_propcodes_view",
        "/settings/segments": "settings_segments_master_page_access",
        "/settings/books": "settings_books_page_access",
        "/admin/submissions": "settings_company_page_access",
        "/admin/announcements": "announcement_page_access",
        "/support": "report_view",
        "/admin/support-forwarded": "report_view",
        "/admin/email-communications": "email_log_view",
    };

    // Routes that are only accessible when viewing as Proda company (and also require admin)
    const prodaOnlyRoutes = [
        "/admin/companies",
        "/admin/global-audit-log",
        "/admin/support-forwarded",
    ];

    // ==========================================
    // Root Layout Component
    // ==========================================
    const RootLayout = () => {
        const storeUser = useAppStore((state) => state.user);
        const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
        const effectiveCompanyName = useAppStore((state) => state.effectiveCompanyName);
        const effectiveSubgroup = useAppStore((state) => state.effectiveSubgroup);
        const availableCompanies = useAppStore((state) => state.availableCompanies);
        const { user: authUser, logout, isAuthenticated, getAccessToken } = useAuth();
        const [isUserLoading, setIsUserLoading] = useState(true);
        const [accessError, setAccessError] = useState<{
            error: string;
            errorMessage: string;
            email?: string;
        } | null>(null);
        // Tracks the last (user, emulated-subgroup, effective-company)
        // combination fetched from /auth/me, so a change in emulation or in
        // the company viewed-as triggers a refetch with the matching headers:
        // X-Effective-Subgroup de-escalates permissions to the emulated
        // subgroup (strict emulation), and X-Company-ID makes the response
        // carry the viewed company's feature flags rather than Proda's own.
        const lastFetchKeyRef = useRef<string | null>(null);
        // Bumped after the caller's own feature flags change, to refetch /auth/me.
        const [meRefreshNonce, setMeRefreshNonce] = useState(0);
        const navigate = useNavigate();
        const location = useLocation();

        // Sync Auth0 user with store - must complete before rendering children
        useEffect(() => {
            // Only fetch if authenticated with Auth0
            if (!isAuthenticated) {
                setIsUserLoading(false);
                lastFetchKeyRef.current = null;
                return;
            }

            // Wait for authUser to be available
            if (!authUser) {
                return;
            }

            // Refetch when the user, the emulated subgroup OR the company
            // viewed-as changes.
            const fetchKey = `${authUser.id ?? ""}:${effectiveSubgroup ?? ""}:${effectiveCompanyId ?? ""}:${meRefreshNonce}`;
            if (lastFetchKeyRef.current === fetchKey) {
                return;
            }

            // Fetch user details from backend and update store
            const fetchUserDetails = async () => {
                // Mark as fetched to prevent duplicate calls for this combination
                lastFetchKeyRef.current = fetchKey;

                try {
                    const token = await getAccessToken();
                    if (token) {
                        // Store the access token for API calls
                        store.getState().setAccessToken(token);

                        const headers: Record<string, string> = {
                            Authorization: `Bearer ${token}`,
                        };
                        // Emulation must be strict: ask the backend for the
                        // emulated subgroup's permissions so the UI matches what
                        // the API will actually authorize.
                        if (effectiveSubgroup) {
                            headers["X-Effective-Subgroup"] = effectiveSubgroup;
                        }
                        // View-as must be strict too: the backend reports the
                        // feature flags of the company named here, which is
                        // the company its flag-gated endpoints will check.
                        if (effectiveCompanyId) {
                            headers["X-Company-ID"] = effectiveCompanyId;
                        }
                        const response = await fetch("/api/v1/auth/me", { headers });
                        if (response.ok) {
                            const userData = await response.json();

                            // Check for access errors (e.g., unsupported email domain)
                            if (userData.error) {
                                setAccessError({
                                    error: userData.error,
                                    errorMessage:
                                        userData.error_message ||
                                        "Your organization is not supported. Please contact support@proda.ai for assistance.",
                                    email: userData.email,
                                });
                                setIsUserLoading(false);
                                return;
                            }

                            // Check if user is a Proda user (by email domain OR company)
                            const email = userData.email || authUser.email || "";
                            const companyName = userData.company_name || "";
                            const isProdaUser =
                                email.toLowerCase().endsWith("@proda.ai") ||
                                companyName.toLowerCase() === "proda";

                            const isAdmin = userData.is_admin ?? false;
                            const permissions: string[] = userData.permissions ?? [];

                            store.getState().setUser({
                                id: userData.id || authUser.id || "",
                                email: email,
                                displayName: userData.display_name || authUser.displayName || "",
                                isAdmin,
                                companyId: userData.company_id || "",
                                companyName: userData.company_name || "",
                                isProdaUser,
                                role: userData.role ?? "admin",
                                subgroup: userData.subgroup ?? null,
                                permissions,
                                roleLabels: userData.role_labels ?? {},
                                subgroupLabels: userData.subgroup_labels ?? {},
                                subgroupRoles: userData.subgroup_roles ?? {},
                                emulationTargets: userData.emulation_targets ?? [],
                                assignableSubgroups: userData.assignable_subgroups ?? [],
                                companyFeatureFlags: userData.company_feature_flags ?? {},
                                environmentFeatureFlags: userData.environment_feature_flags ?? {},
                            });

                            // Stale-state cleanup. Base this on emulation_targets,
                            // which the backend always derives from the REAL
                            // subgroup (not the emulated one) — so it stays correct
                            // even while permissions are de-escalated. Clear emulation
                            // if the user cannot emulate anyone, or the current target
                            // is no longer permitted.
                            const emulationTargets: string[] = userData.emulation_targets ?? [];
                            const currentEffective = store.getState().effectiveSubgroup;
                            if (
                                emulationTargets.length === 0 ||
                                (currentEffective !== null &&
                                    !emulationTargets.includes(currentEffective))
                            ) {
                                store.getState().setEffectiveSubgroup(null);
                            }

                            // For Proda admin users, fetch all available companies for switching
                            // Only fetch if user is both a Proda user AND an admin (endpoint requires admin)
                            if (isProdaUser && isAdmin) {
                                try {
                                    const companiesResponse = await api.companies.list({
                                        is_active: true,
                                        limit: 1000,
                                    });
                                    const activeCompanies = companiesResponse.items.map((c) => ({
                                        id: c.id,
                                        name: c.name,
                                        slug: c.slug,
                                        code: c.code,
                                        isActive: c.is_active,
                                        logoUrl: c.logo_url,
                                    }));
                                    store.getState().setAvailableCompanies(activeCompanies);

                                    // If user has an effective company that's no longer active, reset it
                                    const currentEffectiveCompanyId =
                                        store.getState().effectiveCompanyId;
                                    if (currentEffectiveCompanyId) {
                                        const isEffectiveCompanyActive = activeCompanies.some(
                                            (c) => c.id === currentEffectiveCompanyId && c.isActive,
                                        );
                                        if (!isEffectiveCompanyActive) {
                                            store.getState().setEffectiveCompany(null, null);
                                        }
                                    }
                                } catch (err) {
                                    // Silently fail for 403 (not admin) - don't spam console
                                    // Check if it's an ApiError with 403 status
                                    if (err instanceof ApiError && err.status === 403) {
                                        // User is not admin, skip company fetching - this is expected
                                        // Don't log or retry
                                        return;
                                    }
                                    // Only log unexpected errors
                                    console.error("Failed to fetch available companies:", err);
                                }
                            }
                        } else if (response.status === 403) {
                            // A deactivated account returns 403 USER_INACTIVE from
                            // every authenticated endpoint, /auth/me included. The
                            // block above is skipped on any non-2xx, so detect it
                            // here and show a clear page instead of dropping the
                            // user into an empty, broken app.
                            const body = await response.json().catch(() => null);
                            if (body?.error?.code === "USER_INACTIVE") {
                                setAccessError({
                                    error: "USER_INACTIVE",
                                    errorMessage:
                                        body.error.message ||
                                        "Your account has been deactivated. Please contact your administrator.",
                                    email: authUser.email ?? undefined,
                                });
                                setIsUserLoading(false);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch user details:", error);
                    lastFetchKeyRef.current = null; // Reset on error to allow retry
                    // Use Auth0 user info as fallback
                    const email = authUser.email || "";
                    store.getState().setUser({
                        id: authUser.id || "",
                        email: email,
                        displayName: authUser.displayName || "",
                        isAdmin: false,
                        companyId: "",
                        companyName: "",
                        isProdaUser: email.toLowerCase().endsWith("@proda.ai"),
                        role: "admin",
                        subgroup: null,
                        permissions: [],
                        roleLabels: {},
                        subgroupLabels: {},
                        subgroupRoles: {},
                        emulationTargets: [],
                        assignableSubgroups: [],
                        companyFeatureFlags: {},
                        environmentFeatureFlags: {},
                    });
                } finally {
                    setIsUserLoading(false);
                }
            };
            fetchUserDetails();
        }, [isAuthenticated, authUser?.id, effectiveSubgroup, effectiveCompanyId, meRefreshNonce]);

        // A user deactivated mid-session gets 403 USER_INACTIVE on subsequent API
        // calls; the client notifies here so we replace the app with the same
        // full-page notice instead of showing per-view permission errors.
        useEffect(() => {
            tokenService.setInactiveAccountHandler(({ message }) => {
                setAccessError({
                    error: "USER_INACTIVE",
                    errorMessage: message,
                    email: authUser?.email ?? undefined,
                });
                setIsUserLoading(false);
            });
            return () => tokenService.clearInactiveAccountHandler();
        }, [authUser?.email]);

        const handleLogout = useCallback(() => {
            store.getState().clearUser();
            setAccessError(null);
            logout();
        }, [logout]);

        // Helper to check if current company is Proda
        const getIsProdaCompany = useCallback(
            (companyId: string | null) => {
                if (!companyId) {
                    // No effective company, use user's company
                    const userCompanyName = storeUser?.companyName;
                    return userCompanyName?.toLowerCase() === "proda";
                }
                const company = availableCompanies.find((c) => c.id === companyId);
                return (
                    company?.name?.toLowerCase() === "proda" ||
                    company?.code?.toUpperCase() === "PRODA"
                );
            },
            [availableCompanies, storeUser?.companyName],
        );

        // Admin status follows emulation: emulating a non-admin subgroup drops
        // admin chrome and admin-route access, matching the emulated persona.
        const isViewingAsAdmin = computeIsViewingAsAdmin(storeUser, effectiveSubgroup);

        // Redirect to dashboard if current route becomes inaccessible after company/subgroup switch
        useEffect(() => {
            // Don't check route access while user is still loading
            // This prevents redirect on page refresh before user data is loaded
            if (isUserLoading) {
                return;
            }

            const isProdaCompany = getIsProdaCompany(effectiveCompanyId);
            const isOnProdaOnlyRoute = prodaOnlyRoutes.some((route) =>
                location.pathname.startsWith(route),
            );
            const isOnAdminOnlyRoute = adminOnlyRoutes.some((route) =>
                location.pathname.startsWith(route),
            );

            // Redirect from Proda-only routes when not viewing as Proda company
            if (isOnProdaOnlyRoute && !isProdaCompany) {
                navigate("/", { replace: true });
                return;
            }

            // Redirect from admin routes when not viewing as admin
            if ((isOnAdminOnlyRoute || isOnProdaOnlyRoute) && !isViewingAsAdmin) {
                navigate("/", { replace: true });
                return;
            }

            // Deal route gating, narrowest first:
            //  - create (.../deals/new) requires deal_create
            //  - the deals list (.../deals) requires only deal_view, which every
            //    subgroup holds — so a Business Partner can view the deals of the
            //    business partner they belong to (the backend scopes the list to
            //    their own deals).
            //  - a deal's detail/overview pages (.../deals/{id}) still require
            //    deal_overview_view, which BP/BP+ do not have.
            const isDealCreateRoute =
                /^\/admin\/(business-partners|portfolio-companies)\/[^/]+\/deals\/new$/.test(
                    location.pathname,
                );
            const isDealListRoute =
                /^\/admin\/(business-partners|portfolio-companies)\/[^/]+\/deals\/?$/.test(
                    location.pathname,
                );
            const isOnDealRoute =
                /^\/admin\/(business-partners|portfolio-companies)\/[^/]+\/deals/.test(
                    location.pathname,
                );
            if (isDealCreateRoute) {
                if (!storeUser?.permissions?.includes("deal_create")) {
                    navigate("/", { replace: true });
                    return;
                }
            } else if (isDealListRoute) {
                if (!isViewingAsAdmin && !storeUser?.permissions?.includes("deal_view")) {
                    navigate("/", { replace: true });
                    return;
                }
            } else if (isOnDealRoute) {
                if (!isViewingAsAdmin && !storeUser?.permissions?.includes("deal_overview_view")) {
                    navigate("/", { replace: true });
                    return;
                }
            }

            // Business Partners list and details are reachable by any subgroup with
            // page access (not only admins); gate on that permission, not admin role.
            if (
                location.pathname.startsWith("/admin/business-partners") &&
                !storeUser?.permissions?.includes("business_partners_page_access")
            ) {
                navigate("/", { replace: true });
                return;
            }

            // Audit log requires the audit_log_view permission
            if (
                location.pathname.startsWith("/admin/audit-log") &&
                !storeUser?.permissions?.includes("audit_log_view")
            ) {
                navigate("/", { replace: true });
                return;
            }

            // Email communications additionally requires the company feature flag,
            // except for Proda users (who may view-as any company).
            if (
                location.pathname.startsWith("/admin/email-communications") &&
                !storeUser?.isProdaUser &&
                !storeUser?.companyFeatureFlags?.email_communications_enabled
            ) {
                navigate("/", { replace: true });
                return;
            }

            // Homepage v2 is opt-in per company while it rolls out; without
            // the flag the board is not reachable and the legacy file lists
            // are still the landing pages.
            if (
                location.pathname.startsWith(routerPaths.home) &&
                !storeUser?.companyFeatureFlags?.homepage_v2_enabled
            ) {
                navigate(
                    resolveLandingPath(storeUser?.permissions ?? [], { homepageEnabled: false }),
                    { replace: true },
                );
                return;
            }

            // Settings routes are gated per-permission (not admin-only)
            for (const [route, required] of Object.entries(routePermissions)) {
                if (location.pathname.startsWith(route)) {
                    if (!storeUser?.permissions?.includes(required)) {
                        navigate("/", { replace: true });
                        return;
                    }
                    break;
                }
            }
        }, [
            isUserLoading,
            effectiveCompanyId,
            effectiveSubgroup,
            location.pathname,
            getIsProdaCompany,
            navigate,
            isViewingAsAdmin,
            storeUser?.permissions,
            storeUser?.isProdaUser,
            storeUser?.companyFeatureFlags,
        ]);

        // Handlers for context switching
        const handleCompanySwitch = useCallback(
            (companyId: string | null) => {
                if (!companyId) {
                    store.getState().setEffectiveCompany(null, null);
                } else {
                    const company = availableCompanies.find((c) => c.id === companyId);
                    store.getState().setEffectiveCompany(companyId, company?.name || null);
                }
            },
            [availableCompanies],
        );

        const handleSubgroupSwitch = useCallback((subgroup: string | null) => {
            store.getState().setEffectiveSubgroup(subgroup);
            // Reload so the backend re-evaluates permissions under the emulated subgroup
            window.location.reload();
        }, []);

        // Settings modal state
        const [isSettingsOpen, setIsSettingsOpen] = useState(false);
        // Own feature flags (Proda users only). Saving bumps `meRefreshNonce`
        // so /auth/me is refetched and the sidebar/route gates pick up the
        // new effective flags without a reload.
        const [isOwnFlagsOpen, setIsOwnFlagsOpen] = useState(false);
        const [ownFlags, setOwnFlags] = useState<TUserFeatureFlagsData | null>(null);
        const [ownFlagsError, setOwnFlagsError] = useState<string | null>(null);
        const openOwnFlags = useCallback(() => {
            const userId = storeUser?.id;
            if (!userId) return;
            setIsSettingsOpen(false);
            setOwnFlags(null);
            setOwnFlagsError(null);
            setIsOwnFlagsOpen(true);
            api.users
                .getFeatureFlags(userId)
                .then((flags) =>
                    setOwnFlags({
                        overrides: flags.overrides,
                        companyFlags: flags.company_flags,
                        effectiveFlags: flags.effective_flags,
                    }),
                )
                .catch((err: unknown) =>
                    setOwnFlagsError(
                        err instanceof Error ? err.message : "Failed to load feature flags",
                    ),
                );
        }, [storeUser?.id]);
        const saveOwnFlags = useCallback(
            async (overrides: Record<string, boolean | null>) => {
                const userId = storeUser?.id;
                if (!userId) return;
                await api.users.updateFeatureFlags(userId, { overrides });
                setMeRefreshNonce((n) => n + 1);
            },
            [storeUser?.id],
        );
        const handleSettingsClick = useCallback(() => {
            setIsSettingsOpen(true);
        }, []);

        // Compute the display company name (effective or actual)
        const displayCompanyName = effectiveCompanyName || storeUser?.companyName;
        const displayCompanyId = effectiveCompanyId || storeUser?.companyId;

        // Announcement banner state (must be before early returns to follow Rules of Hooks)
        const [activeAnnouncement, setActiveAnnouncement] = useState<{
            id: string;
            title: string;
            message: string;
            type: "info" | "warning";
            is_active: boolean;
        } | null>(null);
        const [isLoadingAnnouncement, setIsLoadingAnnouncement] = useState(false);

        // Fetch active announcement
        const loadActiveAnnouncement = useCallback(async () => {
            if (!isAuthenticated || !storeUser?.id) {
                setActiveAnnouncement(null);
                return;
            }

            setIsLoadingAnnouncement(true);
            try {
                const announcement = await api.announcements.getActive();
                if (announcement) {
                    setActiveAnnouncement({
                        id: announcement.id,
                        title: announcement.title,
                        message: announcement.message,
                        type: announcement.type,
                        is_active: announcement.is_active,
                    });
                } else {
                    setActiveAnnouncement(null);
                }
            } catch (error) {
                console.error("Failed to load active announcement:", error);
                setActiveAnnouncement(null);
            } finally {
                setIsLoadingAnnouncement(false);
            }
        }, [isAuthenticated, storeUser?.id]);

        // Load announcement when user/company changes
        useEffect(() => {
            loadActiveAnnouncement();
        }, [loadActiveAnnouncement, displayCompanyId, storeUser?.id]);

        // Handle announcement dismiss
        const handleDismissAnnouncement = useCallback(
            async (announcementId: string) => {
                try {
                    await api.announcements.dismiss(announcementId);
                    // Reload to get updated announcement (or null if none eligible)
                    await loadActiveAnnouncement();
                } catch (error) {
                    console.error("Failed to dismiss announcement:", error);
                    throw error;
                }
            },
            [loadActiveAnnouncement],
        );

        // Wait for user to be loaded before rendering children
        // This ensures company ID is available for API calls
        if (isUserLoading) {
            return (
                <div className="flex h-screen items-center justify-center">
                    <Loader size="lg" />
                </div>
            );
        }

        // Show a full-page notice when the user cannot use the app: a deactivated
        // account (403 USER_INACTIVE) or an unsupported email domain.
        if (accessError) {
            return (
                <AccessErrorPage
                    title={
                        accessError.error === "USER_INACTIVE"
                            ? "Account Deactivated"
                            : "Access Not Available"
                    }
                    email={accessError.email}
                    errorMessage={accessError.errorMessage}
                    onLogout={handleLogout}
                />
            );
        }

        // Get company logo URL (effective company's logo or user's company logo)
        const displayCompanyLogoUrl = effectiveCompanyId
            ? availableCompanies.find((c) => c.id === effectiveCompanyId)?.logoUrl || null
            : storeUser?.companyLogoUrl || null;

        // All file pages render full width (minimal horizontal padding, no
        // centered max-width) so the wide FilesTable — and the spreadsheet-heavy
        // File Review / Generated GL Lines screens — can use the entire content
        // area. This covers the list pages (All Files, TB/GL, FinJournals,
        // Supporting Files, Archive) as well as `/files/:fileId` and
        // `/files/:fileId/gl-lines`. The Homepage v2 board needs the same
        // treatment for its 3-column layout. Non-file/non-home routes keep the
        // centered container.
        const isFullWidthRoute =
            location.pathname === "/files" ||
            location.pathname.startsWith("/files/") ||
            location.pathname === "/home";

        return (
            <>
                <MainLayout
                    userName={storeUser?.displayName || authUser?.displayName}
                    companyName={displayCompanyName}
                    companyLogoUrl={displayCompanyLogoUrl}
                    isAdmin={isViewingAsAdmin}
                    isProdaUser={storeUser?.isProdaUser}
                    companyFeatureFlags={storeUser?.companyFeatureFlags}
                    permissions={storeUser?.permissions ?? []}
                    emulationTargets={storeUser?.emulationTargets ?? []}
                    subgroupLabels={storeUser?.subgroupLabels ?? {}}
                    effectiveSubgroup={effectiveSubgroup}
                    userSubgroup={storeUser?.subgroup ?? null}
                    effectiveCompanyId={displayCompanyId}
                    availableCompanies={availableCompanies}
                    onCompanySwitch={handleCompanySwitch}
                    onSubgroupSwitch={handleSubgroupSwitch}
                    onSettingsClick={handleSettingsClick}
                    onLogout={handleLogout}
                >
                    <div className="min-h-0 flex-1 overflow-auto">
                        <AnnouncementBanner
                            announcement={activeAnnouncement}
                            onDismiss={handleDismissAnnouncement}
                            isLoading={isLoadingAnnouncement}
                        />
                        <div className={isFullWidthRoute ? "w-full px-4 py-6" : "container py-6"}>
                            <Outlet />
                        </div>
                    </div>
                </MainLayout>

                {/* Feedback Button */}
                <FeedbackButton
                    onSubmit={(data) => api.supportTickets.create(data)}
                    canSubmitBug={storeUser?.permissions?.includes("report_issue_submit") ?? false}
                    canViewTickets={storeUser?.permissions?.includes("report_view") ?? false}
                />

                {/* Settings Modal */}
                <ViewSettings
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    user={storeUser}
                    effectiveCompanyName={effectiveCompanyName}
                    effectiveSubgroup={effectiveSubgroup}
                    onOpenFeatureFlags={storeUser?.isProdaUser ? openOwnFlags : undefined}
                />

                {/* Own feature flags (Proda users only) */}
                {storeUser?.isProdaUser && (
                    <FeatureFlagsModal
                        open={isOwnFlagsOpen}
                        onClose={() => setIsOwnFlagsOpen(false)}
                        subjectLabel="you"
                        data={ownFlags}
                        loadError={ownFlagsError}
                        onSave={saveOwnFlags}
                    />
                )}
            </>
        );
    };

    /**
     * The "/" index route. Which page a user lands on depends on their
     * permissions and on whether their company has the Homepage v2 board
     * enabled, so it is resolved here at render time instead of being a fixed
     * redirect in the route table.
     */
    const LandingRedirect: React.FC = () => {
        const storeUser = useAppStore((state) => state.user);
        // Wait for the user (and with them the company's feature flags) rather
        // than redirecting on a guess and bouncing the page a moment later.
        if (!storeUser) return null;
        return (
            <Navigate
                to={resolveLandingPath(storeUser?.permissions ?? [], {
                    homepageEnabled: Boolean(storeUser?.companyFeatureFlags?.homepage_v2_enabled),
                })}
                replace
            />
        );
    };

    return { RootLayout, LandingRedirect, adminOnlyRoutes, prodaOnlyRoutes };
};
